import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import { 
    getAllClients, 
    updateClient, 
    deleteClient, 
    getClientStats, 
    getClientChatHistory, 
    impersonateClient,
    getAllConsultants,
    processClientOverage
} from '../../services/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faEdit, faTrash, faPlus, faTimes, faCheck, 
    faChartBar, faHistory, faUserSecret, faEye,
    faChevronLeft, faChevronRight, faSearch,
    faSort, faSortUp, faSortDown
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { useImpersonation } from '../../contexts/ImpersonationContext';
import { useTable, usePagination, useGlobalFilter, useSortBy } from 'react-table';

interface Client {
    id: string;
    name: string;
    email: string;
    phone?: string;
    website_url?: string;
    status?: string;
    created_at: string;
    config?: any;
    consultant_id?: string;
    consultant_name?: string;
}

interface OverageRunSummary {
    total_clients: number;
    total_amount_cents: number;
    successful: number;
    failed: number;
    skipped: number;
    clients: Array<{
        client_id: string;
        amount_cents: number;
        messages_overage: number;
        status: string;
        error?: string;
    }>;
    client_filter?: string | null;
}

interface OverageProcessResponse {
    success: boolean;
    dry_run: boolean;
    summary: OverageRunSummary;
    message?: string;
}

interface ClientStats {
    chat?: {
        message_count: number;
        first_message: string;
        last_message: string;
        total_conversations?: number;
        avg_response_time_seconds?: number;
    };
    embeddings?: {
        total_chunks: number;
        total_tokens: number;
        total_cost: number;
    };
    citations?: {
        total_citations: number;
        total_queries: number;
        total_sources: number;
    };
    tokens?: {
        total_requests: number;
        total_tokens: number;
        total_cost: number;
        services: Array<{
            service_type: string;
            request_count: number;
            total_tokens: number;
            total_cost: number;
            avg_cost_per_request: number;
        }>;
    };
    billing?: {
        has_subscription: boolean;
        plan_name: string;
        plan_tier: string;
        messages_used: number;
        messages_limit: number;
        messages_overage: number;
        overage_messages: number;
        usage_percentage: number;
        base_cost: number;
        overage_cost: number;
        overage_cost_cents: number;
        total_cost: number;
        total_cost_cents: number;
        overage_rate_per_message: number;
        period_start?: string;
        period_end?: string;
        subscription_status?: string;
    };
}

interface ChatMessage {
    id: string;
    user_message: string;
    assistant_response: string;
    created_at: string;
    metadata?: any;
    conversation_id?: string;
}

const ConsultantDashboard: React.FC = () => {
    const navigate = useNavigate();
    const { startImpersonation } = useImpersonation();
    const [clients, setClients] = useState<Client[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [currentClient, setCurrentClient] = useState<Client | null>(null);
    const [editFormData, setEditFormData] = useState<Partial<Client>>({});
    const [consultants, setConsultants] = useState<Array<{ id: string; name: string }>>([]);
    const [userRole] = useState(localStorage.getItem('user_role') || 'client');
    const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | 'all'>('active');
    
    // State for stats and chat history
    const [showStatsModal, setShowStatsModal] = useState(false);
    const [currentStats, setCurrentStats] = useState<ClientStats | null>(null);
    const [loadingStats, setLoadingStats] = useState(false);
    const [processingOverage, setProcessingOverage] = useState(false);
    const [overagePreview, setOveragePreview] = useState<OverageProcessResponse | null>(null);
    const [showOverageConfirm, setShowOverageConfirm] = useState(false);
    const [pendingOverage, setPendingOverage] = useState<OverageRunSummary['clients'][number] | null>(null);
    
    const [showChatModal, setShowChatModal] = useState(false);
    const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
    const [loadingChat, setLoadingChat] = useState(false);
    const [chatPage, setChatPage] = useState(1);
    const [totalChatPages, setTotalChatPages] = useState(1);

    useEffect(() => {
        fetchClients();
        if (userRole === 'super') {
            fetchConsultants();
        }
    }, []);

    const fetchClients = async () => {
        setPendingOverage(null);
        setShowOverageConfirm(false);

        try {
            const data = await getAllClients();
            setClients(data);
        } catch (err) {
            console.error('Error fetching clients:', err);
            toast.error('Failed to fetch clients');
            setError('Failed to fetch clients');
        } finally {
            setLoading(false);
        }
    };

    const fetchConsultants = async () => {
        try {
            const response = await getAllConsultants();
            setConsultants(response.consultants || []);
        } catch (err) {
            console.error('Error fetching consultants:', err);
            toast.error('Failed to fetch consultants');
        }
    };

    const handleDelete = async (clientId: string) => {
        if (window.confirm('Are you sure you want to delete this client? This will remove all associated data and cannot be undone.')) {
            try {
                await deleteClient(clientId);
                toast.success('Client deleted successfully');
                fetchClients();
            } catch (err) {
                console.error('Error deleting client:', err);
                toast.error('Failed to delete client');
            }
        }
    };

    const handleStatusToggle = async (client: Client) => {
        try {
            const newStatus = client.status === 'active' ? 'inactive' : 'active';
            await updateClient(client.id, { status: newStatus });
            toast.success(`Client ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
            fetchClients();
        } catch (err) {
            console.error('Error updating client status:', err);
            toast.error('Failed to update client status');
        }
    };

    const openEditModal = (client: Client) => {
        setCurrentClient(client);
        setEditFormData({
            name: client.name,
            email: client.email,
            phone: client.phone || '',
            website_url: client.website_url || '',
            consultant_id: client.consultant_id || ''
        });
        setShowEditModal(true);
    };

    const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setEditFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSaveEdit = async () => {
        if (!currentClient) return;
        
        try {
            await updateClient(currentClient.id, editFormData);
            toast.success('Client updated successfully');
            setShowEditModal(false);
            fetchClients();
        } catch (err) {
            console.error('Error updating client:', err);
            toast.error('Failed to update client');
        }
    };

    const loadClientStats = async (clientId: string) => {
        setLoadingStats(true);
        try {
            const stats = await getClientStats(clientId);
            setCurrentStats(stats);
        } catch (err) {
            console.error('Error fetching client stats:', err);
            toast.error('Failed to fetch client statistics');
            setCurrentStats(null);
        } finally {
            setLoadingStats(false);
        }
    };

    const openStatsModal = (client: Client) => {
        setCurrentClient(client);
        setCurrentStats(null);
        setOveragePreview(null);
        setShowStatsModal(true);
        loadClientStats(client.id);
    };

    const handleCloseStatsModal = () => {
        setShowStatsModal(false);
        setProcessingOverage(false);
        setOveragePreview(null);
        setShowOverageConfirm(false);
        setPendingOverage(null);
    };

    const openChatHistoryModal = async (clientId: string) => {
        setChatHistory([]);
        setLoadingChat(true);
        setShowChatModal(true);
        setChatPage(1);
        
        await fetchChatHistory(clientId, 1);
    };

    const fetchChatHistory = async (clientId: string, page: number) => {
        try {
            const data = await getClientChatHistory(clientId, page);
            setChatHistory(data.messages);
            setChatPage(data.page);
            setTotalChatPages(data.pages);
        } catch (err) {
            console.error('Error fetching chat history:', err);
            toast.error('Failed to fetch chat history');
        } finally {
            setLoadingChat(false);
        }
    };

    const prepareOverageCharge = async () => {
        if (!currentClient) {
            toast.error('Select a client to process overage billing.');
            return;
        }

        try {
            setProcessingOverage(true);

            const dryRunResponse = await processClientOverage(currentClient.id, true) as OverageProcessResponse;
            setOveragePreview(dryRunResponse);

            const clientSummary = dryRunResponse?.summary?.clients?.find((entry) => entry.client_id === currentClient.id);

            if (!clientSummary || (clientSummary.amount_cents || 0) <= 0) {
                toast.info('Dry run completed. No overage detected for this client.');
                return;
            }

            setPendingOverage(clientSummary);
            setShowOverageConfirm(true);
        } catch (err: any) {
            console.error('Error preparing overage billing:', err);
            const message = err?.detail || err?.message || 'Failed to prepare overage billing';
            toast.error(message);
        } finally {
            setProcessingOverage(false);
        }
    };

    const handleProcessOverage = async () => {
        if (!currentClient) {
            toast.error('Select a client to process overage billing.');
            return;
        }

        try {
            setProcessingOverage(true);

            const processResponse = await processClientOverage(currentClient.id, false) as OverageProcessResponse;
            setOveragePreview(processResponse);
            toast.success('Overage invoice item queued successfully.');

            setShowOverageConfirm(false);
            setPendingOverage(null);

            await loadClientStats(currentClient.id);
        } catch (err: any) {
            console.error('Error processing overage billing:', err);
            const message = err?.detail || err?.message || 'Failed to process overage billing';
            toast.error(message);
        } finally {
            setProcessingOverage(false);
        }
    };

    const handleImpersonateClient = async (clientId: string) => {
        try {
            const data = await impersonateClient(clientId);

            // Use the impersonation context to manage the state
            startImpersonation(data);

            // Update localStorage with the impersonated client's details
            localStorage.setItem('client_id', clientId);
            localStorage.setItem('user_role', 'client');
            if(data.token) {
                localStorage.setItem('token', data.token);
            }

            // Navigate to the client dashboard
            navigate('/dashboard');

            toast.success('Impersonation successful. You are now viewing as the client.');
        } catch (err) {
            console.error('Error impersonating client:', err);
            toast.error('Failed to impersonate client');
        }
    };

    const handleViewWebsite = (website_url?: string) => {
        if (!website_url) {
            toast.error('No website URL available');
            return;
        }
        
        // Ensure URL has http/https prefix
        let url = website_url;
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
            url = 'https://' + url;
        }
        
        window.open(url, '_blank');
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString();
    };

    const formatCurrency = (amountCents?: number) => {
        if (amountCents === undefined || amountCents === null || Number.isNaN(amountCents)) {
            return '$0.00';
        }
        return `$${(amountCents / 100).toFixed(2)}`;
    };

    const formatUsd = (amount?: number) => {
        if (amount === undefined || amount === null || Number.isNaN(amount)) {
            return '$0.00';
        }
        const precision = amount >= 1 ? 2 : 4;
        return `$${amount.toFixed(precision)}`;
    };

    // Filter clients based on status
    const filteredClients = useMemo(() => {
        return clients.filter(client => {
            if (statusFilter === 'all') return true;
            if (statusFilter === 'active') return !client.status || client.status === 'active';
            return client.status === statusFilter;
        });
    }, [clients, statusFilter]);

    const currentClientId = currentClient?.id || null;
    const billingInfo = currentStats?.billing;
    const hasOverage = (billingInfo?.messages_overage || 0) > 0;
    const usagePercent = billingInfo
        ? typeof billingInfo.usage_percentage === 'number'
            ? billingInfo.usage_percentage
            : billingInfo.messages_limit > 0
                ? (billingInfo.messages_used / billingInfo.messages_limit) * 100
                : 0
        : 0;
    const canQueueOverage = userRole === 'super' && hasOverage && Boolean(currentClient);
    const overageSummary = currentClientId && overagePreview?.summary
        ? overagePreview.summary.clients.find((entry) => entry.client_id === currentClientId)
        : undefined;
    const tokenServices = currentStats?.tokens?.services ?? [];
    const tokenTotals = currentStats?.tokens;
    const hasTokenBreakdown = !!(tokenTotals && tokenServices.length > 0);

    const formatServiceName = (name: string) => name.replace(/_/g, ' ');

    // Define columns for react-table
    const columns = useMemo(() => {
        const cols: any[] = [
            {
                Header: 'Name',
                accessor: 'name',
                Cell: ({ value }: any) => (
                    <div className="text-sm font-medium text-gray-900">{value}</div>
                ),
            },
            {
                Header: 'Email',
                accessor: 'email',
                Cell: ({ value }: any) => (
                    <div className="text-sm text-gray-500">{value}</div>
                ),
            },
            {
                Header: 'Phone',
                accessor: 'phone',
                Cell: ({ value }: any) => (
                    <div className="text-sm text-gray-500">{value || '-'}</div>
                ),
            },
            {
                Header: 'Website',
                accessor: 'website_url',
                Cell: ({ value }: any) => {
                    if (!value) return <div className="text-sm text-gray-500">-</div>;
                    const url = value.replace(/^https?:\/\//, '');
                    return (
                        <div className="text-sm text-gray-500 flex items-center">
                            <span className="truncate max-w-xs">{url}</span>
                            <button 
                                onClick={() => handleViewWebsite(value)}
                                className="ml-2 text-blue-500 hover:text-blue-700"
                                title="Open website"
                            >
                                <FontAwesomeIcon icon={faEye} />
                            </button>
                        </div>
                    );
                },
                disableSortBy: true,
            },
        ];

        if (userRole === 'super') {
            cols.push({
                Header: 'Assigned To',
                accessor: 'consultant_name',
                Cell: ({ value }: any) => (
                    <div className="text-sm text-gray-500">{value || 'Not Assigned'}</div>
                ),
            });
        }

        cols.push(
            {
                Header: 'Status',
                accessor: 'status',
                Cell: ({ row }: any) => {
                    const client = row.original;
                    const isActive = client.status === 'active' || !client.status;
                    return (
                        <button
                            onClick={() => handleStatusToggle(client)}
                            className={`px-3 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full ${
                                isActive
                                    ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                                    : 'bg-red-100 text-red-800 hover:bg-red-200'
                            }`}
                        >
                            <FontAwesomeIcon icon={isActive ? faCheck : faTimes} className="mr-1" />
                            {client.status || 'active'}
                        </button>
                    );
                },
                sortType: (a: any, b: any) => {
                    const aStatus = a.original.status || 'active';
                    const bStatus = b.original.status || 'active';
                    return aStatus.localeCompare(bStatus);
                },
            },
            {
                Header: 'Created At',
                accessor: 'created_at',
                Cell: ({ value }: any) => (
                    <div className="text-sm text-gray-500">
                        {new Date(value).toLocaleDateString()}
                    </div>
                ),
            },
            {
                Header: 'Actions',
                Cell: ({ row }: any) => {
                    const client = row.original;
                    return (
                        <div className="flex justify-end space-x-2">
                            <button
                                onClick={() => openStatsModal(client)}
                                className="text-blue-600 hover:text-blue-900"
                                title="View Statistics"
                            >
                                <FontAwesomeIcon icon={faChartBar} />
                            </button>
                            <button
                                onClick={() => openChatHistoryModal(client.id)}
                                className="text-purple-600 hover:text-purple-900"
                                title="View Chat History"
                            >
                                <FontAwesomeIcon icon={faHistory} />
                            </button>
                            <button
                                onClick={() => handleImpersonateClient(client.id)}
                                className="text-indigo-600 hover:text-indigo-900"
                                title="Impersonate Client"
                            >
                                <FontAwesomeIcon icon={faUserSecret} />
                            </button>
                            <button
                                onClick={() => openEditModal(client)}
                                className="text-green-600 hover:text-green-900"
                                title="Edit Client"
                            >
                                <FontAwesomeIcon icon={faEdit} />
                            </button>
                            <button
                                onClick={() => handleDelete(client.id)}
                                className="text-red-600 hover:text-red-900"
                                title="Delete Client"
                            >
                                <FontAwesomeIcon icon={faTrash} />
                            </button>
                        </div>
                    );
                },
                disableSortBy: true,
            }
        );

        return cols;
    }, [userRole]);

    // Custom global filter function
    const globalFilter = useMemo(() => {
        return (rows: any[], columnIds: string[], filterValue: string) => {
            if (!filterValue) return rows;
            
            const searchLower = filterValue.toLowerCase();
            return rows.filter(row => {
                // Search in name, email, website_url, and consultant_name
                const name = row.original.name?.toLowerCase() || '';
                const email = row.original.email?.toLowerCase() || '';
                const website = row.original.website_url?.toLowerCase() || '';
                const consultant = row.original.consultant_name?.toLowerCase() || '';
                
                return name.includes(searchLower) || 
                       email.includes(searchLower) || 
                       website.includes(searchLower) || 
                       consultant.includes(searchLower);
            });
        };
    }, []);

    // Table instance
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize, globalFilter: globalFilterValue },
        setGlobalFilter,
        prepareRow,
    } = useTable(
        {
            columns,
            data: filteredClients,
            initialState: { pageIndex: 0, pageSize: 10 },
            globalFilter,
        },
        useGlobalFilter,
        useSortBy,
        usePagination
    );

    if (loading) {
        return <div className="flex justify-center items-center h-64">Loading...</div>;
    }

    if (error) {
        return <div className="text-red-500 text-center p-4">{error}</div>;
    }

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Consultant Dashboard</h1>
            </div>

            <div className="bg-white rounded-lg shadow mb-8">
                {/* Sticky container for header and controls */}
                <div className="sticky top-0 z-40 bg-white">
                    <div className="p-4 bg-blue-50 border-b border-blue-100 flex justify-between items-center">
                        <div>
                            <h2 className="text-lg font-semibold text-gray-800">Your Clients</h2>
                            <p className="text-sm text-gray-600">Manage all your clients from this dashboard</p>
                        </div>
                        <div className="flex items-center gap-2">
                            <button 
                                onClick={() => navigate('/demo')}
                                className="px-4 py-2 bg-[#d89d08] text-white rounded-lg shadow-md hover:shadow-lg hover:bg-[#d89d08]-700 transition-all duration-200"
                            >
                                Onboard Client
                            </button>
                            <button
                                onClick={() => navigate('/consultant/client-form')}
                                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
                            >
                                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                                Add New Client
                            </button>
                        </div>
                    </div>
                    
                    {/* Status Filter Tabs */}
                    <div className="flex border-b border-gray-200">
                        <button
                            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                                statusFilter === 'active'
                                    ? 'text-blue-600 border-blue-600'
                                    : 'text-gray-500 border-transparent hover:text-gray-700'
                            }`}
                            onClick={() => setStatusFilter('active')}
                        >
                            Active Clients ({clients.filter(c => !c.status || c.status === 'active').length})
                        </button>
                        <button
                            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                                statusFilter === 'inactive'
                                    ? 'text-blue-600 border-blue-600'
                                    : 'text-gray-500 border-transparent hover:text-gray-700'
                            }`}
                            onClick={() => setStatusFilter('inactive')}
                        >
                            Inactive Clients ({clients.filter(c => c.status === 'inactive').length})
                        </button>
                        <button
                            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                                statusFilter === 'all'
                                    ? 'text-blue-600 border-blue-600'
                                    : 'text-gray-500 border-transparent hover:text-gray-700'
                            }`}
                            onClick={() => setStatusFilter('all')}
                        >
                            All Clients ({clients.length})
                        </button>
                    </div>
                </div>
                
                <div className="relative">
                    <style>{`
                        .table-container::-webkit-scrollbar {
                            height: 12px;
                        }
                        .table-container::-webkit-scrollbar:vertical {
                            width: 0;
                        }
                        .table-container::-webkit-scrollbar-track {
                            background: #E5E7EB;
                            border-radius: 6px;
                        }
                        .table-container::-webkit-scrollbar-thumb {
                            background: #065BDF;
                            border-radius: 6px;
                        }
                        .table-container::-webkit-scrollbar-thumb:hover {
                            background: #0547B3;
                        }
                    `}</style>
                    {/* Search and Controls */}
                    <div className="sticky top-32 z-30 bg-white border-b border-gray-200 p-4 shadow-sm">
                        <div className="flex justify-between items-center">
                            <div className="relative">
                                <input
                                    type="text"
                                    value={globalFilterValue || ''}
                                    onChange={(e) => setGlobalFilter(e.target.value)}
                                    placeholder="Search by name, email, website, assigned to..."
                                    className="pl-10 pr-4 py-2 w-80 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                                <FontAwesomeIcon 
                                    icon={faSearch} 
                                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" 
                                />
                            </div>
                            <div className="flex items-center gap-4">
                                <div className="flex items-center gap-3">
                                    <button
                                        onClick={() => {
                                            const table = document.querySelector('.table-container');
                                            if (table) table.scrollBy({ left: -300, behavior: 'smooth' });
                                        }}
                                        className="w-10 h-10 bg-white border border-gray-300 rounded-full flex items-center justify-center hover:bg-gray-50 hover:border-gray-400 transition-all shadow-sm"
                                        title="Scroll left"
                                    >
                                        <FontAwesomeIcon icon={faChevronLeft} className="w-4 h-4 text-gray-700" />
                                    </button>
                                    <div className="text-xs text-gray-500 font-medium uppercase tracking-wide">Scroll</div>
                                    <button
                                        onClick={() => {
                                            const table = document.querySelector('.table-container');
                                            if (table) table.scrollBy({ left: 300, behavior: 'smooth' });
                                        }}
                                        className="w-10 h-10 bg-white border border-gray-300 rounded-full flex items-center justify-center hover:bg-gray-50 hover:border-gray-400 transition-all shadow-sm"
                                        title="Scroll right"
                                    >
                                        <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4 text-gray-700" />
                                    </button>
                                </div>
                                <select
                                    value={pageSize}
                                    onChange={(e) => setPageSize(Number(e.target.value))}
                                    className="border border-gray-300 rounded px-3 py-1 text-sm"
                                >
                                    {[10, 20, 30, 50].map(size => (
                                        <option key={size} value={size}>
                                            Show {size}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto p-4 table-container" style={{
                        scrollbarWidth: 'thin',
                        scrollbarColor: '#065BDF #E5E7EB'
                    }}>
                        <table {...getTableProps()} className="w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                {headerGroups.map((headerGroup: any) => {
                                    const { key, ...headerGroupProps } = headerGroup.getHeaderGroupProps();
                                    return (
                                        <tr key={key} {...headerGroupProps}>
                                            {headerGroup.headers.map((column: any) => {
                                                const { key: columnKey, ...columnProps } = column.getHeaderProps(column.getSortByToggleProps());
                                                return (
                                                    <th
                                                        key={columnKey}
                                                        {...columnProps}
                                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                                    >
                                                <div className="flex items-center">
                                                    {column.render('Header')}
                                                    {column.canSort && (
                                                        <span className="ml-2">
                                                            {column.isSorted ? (
                                                                column.isSortedDesc ? (
                                                                    <FontAwesomeIcon icon={faSortDown} className="text-blue-600" />
                                                                ) : (
                                                                    <FontAwesomeIcon icon={faSortUp} className="text-blue-600" />
                                                                )
                                                            ) : (
                                                                <FontAwesomeIcon icon={faSort} className="text-gray-400" />
                                                            )}
                                                        </span>
                                                    )}
                                                </div>
                                                    </th>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                            </thead>
                            <tbody {...getTableBodyProps()} className="bg-white divide-y divide-gray-200">
                                {page.length === 0 ? (
                                    <tr>
                                        <td colSpan={columns.length} className="px-6 py-4 text-center text-gray-500">
                                            No {statusFilter === 'all' ? '' : statusFilter} clients found.
                                            {statusFilter !== 'all' && ' Try switching to a different status filter.'}
                                        </td>
                                    </tr>
                                ) : (
                                    page.map((row: any) => {
                                        prepareRow(row);
                                        const { key: rowKey, ...rowProps } = row.getRowProps();
                                        return (
                                            <tr key={rowKey} {...rowProps} className="hover:bg-gray-50">
                                                {row.cells.map((cell: any) => {
                                                    const { key: cellKey, ...cellProps } = cell.getCellProps();
                                                    return (
                                                        <td key={cellKey} {...cellProps} className="px-6 py-4 whitespace-nowrap">
                                                            {cell.render('Cell')}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => gotoPage(0)}
                                disabled={!canPreviousPage}
                                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                            >
                                First
                            </button>
                            <button
                                onClick={() => previousPage()}
                                disabled={!canPreviousPage}
                                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                            >
                                Previous
                            </button>
                            <button
                                onClick={() => nextPage()}
                                disabled={!canNextPage}
                                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                            >
                                Next
                            </button>
                            <button
                                onClick={() => gotoPage(pageCount - 1)}
                                disabled={!canNextPage}
                                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                            >
                                Last
                            </button>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-700">
                                Page{' '}
                                <strong>
                                    {pageIndex + 1} of {pageOptions.length}
                                </strong>
                            </span>
                            <span className="text-sm text-gray-700">| Go to page:</span>
                            <input
                                type="number"
                                value={pageIndex + 1}
                                onChange={(e) => {
                                    const page = e.target.value ? Number(e.target.value) - 1 : 0;
                                    gotoPage(page);
                                }}
                                className="border border-gray-300 rounded px-2 py-1 text-sm w-16"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Edit Client Modal */}
            {showEditModal && currentClient && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md mx-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold">Edit Client</h2>
                            <button 
                                onClick={() => setShowEditModal(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={editFormData.name || ''}
                                    onChange={handleEditInputChange}
                                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={editFormData.email || ''}
                                    onChange={handleEditInputChange}
                                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                                <input
                                    type="text"
                                    name="phone"
                                    value={editFormData.phone || ''}
                                    onChange={handleEditInputChange}
                                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                            
                            {userRole === 'super' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Consultant</label>
                                    <select
                                        name="consultant_id"
                                        value={editFormData.consultant_id || ''}
                                        onChange={handleEditInputChange}
                                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="">-- Select Consultant --</option>
                                        {Array.isArray(consultants) && consultants.map(c => (
                                            <option key={c.id} value={c.id}>{c.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            
                            <div className="flex justify-end pt-4">
                                <button
                                    onClick={() => setShowEditModal(false)}
                                    className="bg-gray-300 text-gray-800 px-4 py-2 rounded-md mr-2 hover:bg-gray-400"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSaveEdit}
                                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                                >
                                    Save Changes
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Client Statistics Modal */}
            {showStatsModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
                    <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-auto max-h-[85vh] flex flex-col">
                        <div className="flex items-start justify-between mb-4">
                            <div>
                                <h2 className="text-xl font-bold text-gray-900">Client Statistics</h2>
                                <p className="mt-1 text-xs text-gray-500">
                                    {currentClient ? `${currentClient.name || 'Unknown client'}` : 'Client overview'}
                                    {billingInfo?.plan_name ? ` · Plan: ${billingInfo.plan_name}` : ''}
                                </p>
                            </div>
                            <button
                                onClick={handleCloseStatsModal}
                                className="ml-4 text-gray-500 hover:text-gray-700"
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        {loadingStats ? (
                            <div className="flex flex-1 items-center justify-center">
                                <div className="spinner-border animate-spin h-8 w-8 border-4 border-blue-500 rounded-full border-t-transparent"></div>
                            </div>
                        ) : (
                            <div className="flex-1 overflow-y-auto pr-1">
                                <div className="grid gap-6 md:grid-cols-2">
                                    <section className="space-y-4">
                                        <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-5">
                                            {billingInfo ? (
                                                <>
                                                    {hasOverage && !(overagePreview && overageSummary && !overagePreview.dry_run) && (
                                                        <div className="mb-4 rounded-lg border-l-4 border-rose-500 bg-rose-50 p-4">
                                                            <div className="flex items-start justify-between">
                                                                <div className="flex-1">
                                                                    <div className="flex items-center gap-2 mb-1">
                                                                        <span className="inline-flex items-center rounded-full bg-rose-100 px-2.5 py-0.5 text-xs font-semibold text-rose-800">
                                                                            Overage Detected
                                                                        </span>
                                                                    </div>
                                                                    <p className="text-sm font-semibold text-rose-900 mb-1">
                                                                        Messages over plan: {billingInfo.messages_overage}
                                                                    </p>
                                                                    <p className="text-xs text-rose-700">
                                                                        Additional charge: {formatCurrency(billingInfo.overage_cost_cents ?? billingInfo.overage_cost ?? 0)}
                                                                    </p>
                                                                </div>
                                                                {canQueueOverage && (
                                                                    <button
                                                                        onClick={prepareOverageCharge}
                                                                        disabled={processingOverage}
                                                                        className={`ml-3 inline-flex items-center gap-2 rounded-md bg-rose-600 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-rose-700 ${processingOverage ? 'cursor-not-allowed opacity-70' : ''}`}
                                                                    >
                                                                        {processingOverage ? 'Processing...' : 'Queue Charge'}
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {overagePreview && overageSummary && !overagePreview.dry_run && (
                                                        <div className="mb-4 rounded-lg border-l-4 border-green-500 bg-green-50 p-4">
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-800">
                                                                    Overage Processed
                                                                </span>
                                                            </div>
                                                            <p className="text-sm font-semibold text-green-900 mb-1">
                                                                Overage charge queued successfully
                                                            </p>
                                                            <p className="text-xs text-green-700">
                                                                {formatCurrency(overageSummary.amount_cents)} has been added to the next invoice
                                                            </p>
                                                        </div>
                                                    )}
                                                    <div className="flex flex-col gap-4">
                                                        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                                                            <div>
                                                                <p className="text-xs uppercase tracking-wide text-gray-500">Plan</p>
                                                                <div className="flex flex-wrap items-baseline gap-2">
                                                                    <span className="text-xl font-semibold text-gray-900">{billingInfo.plan_name || 'Unknown'}</span>
                                                                    <span className="text-xs font-medium text-gray-500 uppercase">{billingInfo.plan_tier || 'N/A'}</span>
                                                                </div>
                                                            </div>
                                                            <div className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700">
                                                                <span className="text-[10px] uppercase tracking-wide text-gray-400">Status</span>
                                                                <span className={`inline-flex items-center gap-1 ${billingInfo.subscription_status === 'active' ? 'text-emerald-600' : 'text-amber-600'}`}>
                                                                    <span className={`h-2 w-2 rounded-full ${billingInfo.subscription_status === 'active' ? 'bg-emerald-500' : 'bg-amber-400'}`}></span>
                                                                    {billingInfo.subscription_status || 'unknown'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="space-y-3">
                                                            <div className="space-y-2">
                                                                <div className="flex items-center justify-between text-xs font-medium text-gray-500">
                                                                    <span>Messages</span>
                                                                    <span className="text-sm font-semibold text-gray-900">{billingInfo.messages_limit > 0 ? `${billingInfo.messages_used}/${billingInfo.messages_limit}` : billingInfo.messages_used}{billingInfo.messages_limit > 0 && (<span className="ml-2 text-xs text-gray-500">({usagePercent.toFixed(1)}%)</span>)}</span>
                                                                </div>
                                                                {billingInfo.messages_limit > 0 && (
                                                                    <div className="h-2 w-full rounded-full bg-gray-200">
                                                                        <div className="h-2 rounded-full bg-blue-500 transition-all" style={{ width: `${Math.min(100, usagePercent)}%` }}></div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="rounded-lg border border-gray-100 bg-white px-3 py-2 text-xs">
                                                                <p className="font-medium text-gray-700">Billing period</p>
                                                                <p className="text-sm font-semibold text-gray-900">{`${billingInfo.period_start ? formatDate(billingInfo.period_start) : 'N/A'} – ${billingInfo.period_end ? formatDate(billingInfo.period_end) : 'N/A'}`}</p>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="flex flex-wrap items-center gap-2 rounded-lg bg-gray-50 p-3 text-xs font-medium text-gray-700">
                                                        <span className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 shadow-sm ring-1 ring-gray-200">
                                                            <span className="text-[10px] uppercase text-gray-400">Base</span>
                                                            <span>{formatCurrency(billingInfo.base_cost ?? 0)}</span>
                                                        </span>
                                                        <span className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 shadow-sm ring-1 ring-gray-200">
                                                            <span className="text-[10px] uppercase text-gray-400">Overage</span>
                                                            <span>{formatCurrency(billingInfo.overage_cost_cents ?? billingInfo.overage_cost ?? 0)}</span>
                                                        </span>
                                                        <span className="inline-flex items-center gap-1 rounded-full bg-blue-500/10 px-3 py-1 text-blue-700 shadow-sm ring-1 ring-blue-200">
                                                            <span className="text-[10px] uppercase">Total</span>
                                                            <span>{formatCurrency(billingInfo.total_cost_cents ?? billingInfo.total_cost ?? 0)}</span>
                                                        </span>
                                                    </div>
                                                    {!hasOverage && (
                                                        <div className="flex flex-col gap-1 text-xs">
                                                            <span className="text-emerald-600">No overage this period.</span>
                                                        </div>
                                                    )}
                                                </>
                                            ) : (
                                                <p className="text-sm text-gray-500">No billing data available.</p>
                                            )}
                                        </div>

                                    </section>
                                    <section className="space-y-4">
                                        <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-5">
                                            <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                                                <span className="font-semibold text-gray-800">Token usage</span>
                                                {tokenTotals ? (<>
                                                    <span>{(tokenTotals.total_tokens || 0).toLocaleString()} tokens</span>
                                                    <span>{formatUsd(tokenTotals.total_cost || 0)} spent</span>
                                                    <span>{tokenTotals.total_requests} requests</span>
                                                </>) : (
                                                    <span>No usage yet</span>
                                                )}
                                            </div>
                                            {hasTokenBreakdown ? (
                                                <div className="mt-3 flex flex-wrap gap-2">
                                                    {tokenServices.map((service) => (
                                                        <span
                                                            key={service.service_type}
                                                            className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700"
                                                        >
                                                            <span className="font-semibold text-gray-900">{formatServiceName(service.service_type)}</span>
                                                            <span>{service.total_tokens.toLocaleString()}</span>
                                                            <span title={`${formatUsd(service.total_cost)} spent`} className="text-gray-500">{formatUsd(service.total_cost)}</span>
                                                        </span>
                                                    ))}
                                                    <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
                                                        <span>Total</span>
                                                        <span>{(tokenTotals?.total_tokens || 0).toLocaleString()}</span>
                                                        <span title={`${formatUsd(tokenTotals?.total_cost || 0)} spent`} className="text-blue-500">{formatUsd(tokenTotals?.total_cost || 0)}</span>
                                                    </span>
                                                </div>
                                            ) : null}
                                        </div>
                                    </section>

                                </div>
                            </div>
                        )}
                        <div className="mt-6 flex justify-end">
                            <button
                                onClick={handleCloseStatsModal}
                                className="bg-gray-300 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-400"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showOverageConfirm && pendingOverage && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 px-4">
                    <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
                        <div className="flex items-start justify-between">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">Queue overage charge?</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    This will add the overage to the client's next invoice.
                                </p>
                            </div>
                            <button
                                onClick={() => {
                                    if (!processingOverage) {
                                        setShowOverageConfirm(false);
                                        setPendingOverage(null);
                                    }
                                }}
                                className="ml-4 text-gray-400 hover:text-gray-600"
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        <div className="mt-4 space-y-3 text-sm text-gray-700">
                            <div className="flex justify-between">
                                <span>Messages over plan</span>
                                <span className="font-semibold">{pendingOverage.messages_overage}</span>
                            </div>
                            <div className="flex justify-between">
                                <span>Charge amount</span>
                                <span className="font-semibold">{formatCurrency(pendingOverage.amount_cents)}</span>
                            </div>
                            {billingInfo?.overage_rate_per_message ? (
                                <div className="flex justify-between text-xs text-gray-500">
                                    <span>Rate per message</span>
                                    <span>{formatCurrency(billingInfo.overage_rate_per_message)}</span>
                                </div>
                            ) : null}
                        </div>

                        <div className="mt-6 flex justify-end gap-2">
                            <button
                                onClick={() => {
                                    if (!processingOverage) {
                                        setShowOverageConfirm(false);
                                        setPendingOverage(null);
                                    }
                                }}
                                className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleProcessOverage}
                                disabled={processingOverage}
                                className={`rounded-md px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition ${processingOverage ? 'bg-rose-300 cursor-not-allowed' : 'bg-rose-600 hover:bg-rose-700'}`}
                            >
                                {processingOverage ? 'Queuing…' : 'Confirm charge'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Chat History Modal */}
            {showChatModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-auto max-h-[80vh] flex flex-col">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold">Chat History</h2>
                            <button 
                                onClick={() => setShowChatModal(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        
                        <div className="flex-grow overflow-y-auto">
                            {loadingChat ? (
                                <div className="flex justify-center items-center h-64">
                                    <div className="spinner-border animate-spin h-8 w-8 border-4 border-blue-500 rounded-full border-t-transparent"></div>
                                </div>
                            ) : chatHistory.length === 0 ? (
                                <div className="text-center text-gray-500 py-8">
                                    No chat history available
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    {chatHistory.map((message) => (
                                        <div key={message.id} className="border rounded-lg overflow-hidden">
                                            <div className="bg-gray-50 p-3 border-b">
                                                <div className="flex justify-between items-center">
                                                    <span className="text-sm font-medium text-gray-700">Conversation ID: {message.conversation_id || 'N/A'}</span>
                                                    <span className="text-xs text-gray-500">{formatDate(message.created_at)}</span>
                                                </div>
                                            </div>
                                            <div className="p-4 space-y-4">
                                                <div>
                                                    <div className="text-sm font-medium text-gray-700 mb-1">User:</div>
                                                    <div className="p-3 bg-blue-50 rounded-lg text-gray-700">
                                                        {message.user_message}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="text-sm font-medium text-gray-700 mb-1">Assistant:</div>
                                                    <div className="p-3 bg-gray-50 rounded-lg text-gray-700">
                                                        {message.assistant_response}
                                                    </div>
                                                </div>
                                                {message.metadata && (
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-700 mb-1">Metadata:</div>
                                                        <div className="p-3 bg-gray-100 rounded-lg text-gray-700 text-xs font-mono overflow-x-auto">
                                                            <pre>{JSON.stringify(message.metadata, null, 2)}</pre>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        
                        {totalChatPages > 1 && (
                            <div className="mt-4 flex justify-center space-x-2">
                                <button
                                    onClick={() => {
                                        if (chatPage > 1 && currentClient) {
                                            fetchChatHistory(currentClient.id, chatPage - 1);
                                        }
                                    }}
                                    disabled={chatPage === 1 || loadingChat}
                                    className={`px-3 py-1 rounded-md ${
                                        chatPage === 1 || loadingChat
                                            ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                            : 'bg-blue-500 text-white hover:bg-blue-600'
                                    }`}
                                >
                                    Previous
                                </button>
                                <span className="px-3 py-1 text-gray-700">
                                    Page {chatPage} of {totalChatPages}
                                </span>
                                <button
                                    onClick={() => {
                                        if (chatPage < totalChatPages && currentClient) {
                                            fetchChatHistory(currentClient.id, chatPage + 1);
                                        }
                                    }}
                                    disabled={chatPage === totalChatPages || loadingChat}
                                    className={`px-3 py-1 rounded-md ${
                                        chatPage === totalChatPages || loadingChat
                                            ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                            : 'bg-blue-500 text-white hover:bg-blue-600'
                                    }`}
                                >
                                    Next
                                </button>
                            </div>
                        )}
                        
                        <div className="mt-4 flex justify-end">
                            <button
                                onClick={() => setShowChatModal(false)}
                                className="bg-gray-300 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-400"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ConsultantDashboard; 
